import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { statusInstrumentoOptions } from '../mock';
import { useToast } from '../hooks/use-toast';

const AddInstrumentModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    nome: '',
    marca: '',
    tipo: '',
    proximaCalibracao: '',
    responsavel: '',
    faixaMedicao: '',
    setor: '',
    statusInstrumento: '',
    observacoes: '',
  });
  const { toast } = useToast();

  const parseFlexibleDate = (dateInput) => {
    if (!dateInput) return null;
    
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        return dateInput;
      }
      
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)) {
        const [day, month, year] = dateInput.split('/');
        return `${year}-${month}-${day}`;
      }
      
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)) {
        const date = new Date(dateInput);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }
      
      const date = new Date(dateInput);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      return dateInput;
    } catch (error) {
      return dateInput;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const parsedDate = parseFlexibleDate(formData.proximaCalibracao);

    let status = 'em_estoque';
    if (parsedDate) {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const expiryDate = new Date(parsedDate);
        expiryDate.setHours(0, 0, 0, 0);
        
        if (!isNaN(expiryDate.getTime())) {
          status = expiryDate < today ? 'vencido' : 'em_estoque';
        }
      } catch (error) {
        status = 'em_estoque';
      }
    }

    onAdd({ 
      ...formData, 
      proximaCalibracao: parsedDate || formData.proximaCalibracao,
      status,
      orcamentoNumero: '',
      custoCalibracao: 0,
      custoManutencao: 0,
      qrCodeUrl: '',
    });
    
    toast({
      title: 'Instrumento adicionado!',
      description: 'O instrumento foi cadastrado com sucesso.',
    });
    
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      nome: '',
      marca: '',
      tipo: '',
      proximaCalibracao: '',
      responsavel: '',
      faixaMedicao: '',
      setor: '',
      statusInstrumento: '',
      observacoes: '',
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Adicionar Novo Instrumento</DialogTitle>
          <DialogDescription>
            Preencha os dados do instrumento. Todos os campos são opcionais.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="nome">Nome do Instrumento</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Paquímetro Digital PD-150"
              />
            </div>
            <div>
              <Label htmlFor="marca">Marca</Label>
              <Input
                id="marca"
                value={formData.marca}
                onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                placeholder="Ex: Mitutoyo"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="tipo">Tipo</Label>
              <Input
                id="tipo"
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                placeholder="Ex: Paquímetro, Micrômetro, Balança"
              />
            </div>
            <div>
              <Label htmlFor="faixaMedicao">Faixa de Medição</Label>
              <Input
                id="faixaMedicao"
                value={formData.faixaMedicao}
                onChange={(e) => setFormData({ ...formData, faixaMedicao: e.target.value })}
                placeholder="Ex: 0-150mm, 0-100 bar"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="proximaCalibracao">Data de Vencimento da Calibração</Label>
              <Input
                id="proximaCalibracao"
                value={formData.proximaCalibracao}
                onChange={(e) => setFormData({ ...formData, proximaCalibracao: e.target.value })}
                placeholder="Ex: 2025-12-31 ou 31/12/2025"
              />
              <p className="text-xs text-slate-500 mt-1">
                Aceita formatos: YYYY-MM-DD, DD/MM/YYYY ou outros formatos de data
              </p>
            </div>
            <div>
              <Label htmlFor="responsavel">Responsável</Label>
              <Input
                id="responsavel"
                value={formData.responsavel}
                onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
                placeholder="Ex: Anderson, João Silva"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="setor">Setor</Label>
              <Input
                id="setor"
                value={formData.setor}
                onChange={(e) => setFormData({ ...formData, setor: e.target.value })}
                placeholder="Ex: Produção, Qualidade, Laboratório"
              />
            </div>
            <div>
              <Label htmlFor="statusInstrumento">Status do Instrumento</Label>
              <Select
                value={formData.statusInstrumento}
                onValueChange={(value) => setFormData({ ...formData, statusInstrumento: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  {statusInstrumentoOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              placeholder="Observações adicionais sobre o instrumento"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">
              Adicionar Instrumento
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddInstrumentModal;